/**
 * ProjectStart — /project/start
 * Landing page for project users who have been created by admin but have no project yet.
 * Provides a proper onboarding prompt — NOT a dead-end error page.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, Zap, Sun, Leaf, Truck, Building2, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const PROJECT_TYPES = [
  { type: "housing",    label: "Low-Carbon Housing",   icon: Building2, desc: "Affordable housing with reduced emissions",         color: "text-teal-600 bg-teal-50 border-teal-200"   },
  { type: "solar",      label: "Solar / Renewable",     icon: Sun,       desc: "Solar PV or renewable energy installation",        color: "text-amber-600 bg-amber-50 border-amber-200" },
  { type: "waste",      label: "Biogas / Waste",        icon: Leaf,      desc: "Waste recovery or biogas generation project",      color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  { type: "transport",  label: "Transport / Mobility",  icon: Truck,     desc: "Fleet electrification or modal shift",             color: "text-blue-600 bg-blue-50 border-blue-200"   },
  { type: "efficiency", label: "Energy Efficiency",     icon: Zap,       desc: "Industrial or commercial efficiency upgrade",      color: "text-purple-600 bg-purple-50 border-purple-200" },
  { type: "other",      label: "Other / Custom",        icon: Layers,    desc: "Agriculture, industrial, or custom project type",  color: "text-slate-600 bg-slate-50 border-slate-200" },
];

export default function ProjectStart() {
  const navigate  = useNavigate();
  const { user, currentOrg } = useAuth();
  const [name,     setName]     = useState("");
  const [type,     setType]     = useState(null);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name || !type) {
      toast.error("Please enter a project name and select a project type.");
      return;
    }
    setCreating(true);
    try {
      const res = await client.post("/assessments", {
        name,
        project_type: type,
        org_id:  currentOrg?.id,
        country: currentOrg?.country || "",
        sector:  currentOrg?.sector  || type,
        stage:   "concept",
        status:  "draft",
      });
      // Navigate to the new project workspace
      const id = res.data?.id || res.data?.assessment_id;
      if (id) {
        toast.success(`Project "${name}" created. Welcome to your workspace.`);
        // Housing projects use the housing shell
        if (type === "housing") {
          navigate("/housing-project");
        } else {
          navigate(`/project/${id}/workspace`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create project.");
    }
    setCreating(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="bg-[#0F172A] rounded-xl p-6 mb-6 text-center">
          <Layers size={28} className="text-teal-400 mx-auto mb-3" />
          <PageHeader title="Create Your First Project" />

      <MetricCard label="Project Steps" value={3} color="text-slate-900" />
          <p className="text-xs text-slate-400 mt-2">
            {user?.first_name ? `Welcome, ${user.first_name}.` : "Welcome."}{" "}
            {currentOrg?.name ? `Your organization ${currentOrg.name} is ready.` : "Your workspace is ready."}
            {" "}Let's set up your first project to begin tracking emissions and climate impact.
          </p>
        </div>

        {/* Project name */}
        <SectionCard>

          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            Project Name
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Abuja Solar Housing Estate Phase 1"
            className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 font-medium"
            data-testid="project-name-input"
          />
        
        </SectionCard>

        {/* Project type selection */}
        <SectionCard>

          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Project Type
          </p>
          <div className="grid grid-cols-2 gap-2">
            {PROJECT_TYPES.map(pt => (
              <button key={pt.type}
                onClick={() => setType(pt.type)}
                className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                  type === pt.type ? pt.color + " border-2" : "border-slate-100 hover:border-slate-200 bg-slate-50/50"
                }`}
                data-testid={`project-type-${pt.type}`}>
                <pt.icon size={16} className={`shrink-0 mt-0.5 ${type === pt.type ? "" : "text-slate-400"}`} />
                <div>
                  <p className="text-xs font-bold text-slate-900">{pt.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{pt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        
        </SectionCard>

        <button
          onClick={handleCreate}
          disabled={creating || !name || !type}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-teal-600 text-white font-bold text-sm rounded-xl hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-project-btn">
          {creating
            ? <><RefreshCw size={15} className="animate-spin" /> Creating Project...</>
            : <>Create Project &amp; Enter Workspace <ArrowRight size={15} /></>}
        </button>

        <p className="text-xs text-slate-400 italic text-center mt-3">
          You can create additional projects from within your workspace at any time.
          All project data is platform-governed and auditable.
        </p>
      </div>
    </div>
  );
}
